import { lazy, Suspense } from 'react';
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { isDemoMode } from './config/env';
import { AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppShell } from './layouts/AppShell';
import { PWAInstallBanner } from './components/PWAInstallBanner';
import './components/ProtectedRoute.css';

const lazyPage = <T extends Record<string, any>, K extends keyof T>(
  factory: () => Promise<T>,
  name: K,
) => lazy(() => factory().then((module) => ({ default: module[name] as unknown as React.ComponentType<any> })));

const HomePage = lazyPage(() => import('./pages/HomePage'), 'HomePage');
const SignInPage = lazyPage(() => import('./pages/SignInPage'), 'SignInPage');
const SignUpPage = lazyPage(() => import('./pages/SignUpPage'), 'SignUpPage');
const ResetPasswordPage = lazyPage(() => import('./pages/ResetPasswordPage'), 'ResetPasswordPage');
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'), 'DashboardPage');
const ClassesPage = lazyPage(() => import('./pages/ClassesPage'), 'ClassesPage');
const ClassDetailPage = lazyPage(() => import('./pages/classes/ClassDetailPage'), 'ClassDetailPage');
const SessionDetailPage = lazyPage(() => import('./pages/classes/SessionDetailPage'), 'SessionDetailPage');
const BillingPage = lazyPage(() => import('./pages/BillingPage'), 'BillingPage');
const EventsPage = lazyPage(() => import('./pages/EventsPage'), 'EventsPage');
const NotificationsPage = lazyPage(() => import('./pages/NotificationsPage'), 'NotificationsPage');
const PeoplePage = lazyPage(() => import('./pages/PeoplePage'), 'PeoplePage');
const EnrollmentsPage = lazyPage(() => import('./pages/EnrollmentsPage'), 'EnrollmentsPage');
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'), 'ProfilePage');
const AnalyticsPage = lazyPage(() => import('./pages/AnalyticsPage'), 'AnalyticsPage');
const LeavePage = lazyPage(() => import('./pages/LeavePage'), 'LeavePage');
const InstituteSettingsPage = lazyPage(() => import('./pages/InstituteSettingsPage'), 'InstituteSettingsPage');
const AttendancePage = lazyPage(() => import('./pages/AttendancePage'), 'AttendancePage');
const ActivityTimelinePage = lazyPage(() => import('./pages/ActivityTimelinePage'), 'ActivityTimelinePage');
const VerifyContactPage = lazyPage(() => import('./pages/VerifyContactPage'), 'VerifyContactPage');

const PageFallback = () => (
  <div className="loading-screen" style={{ minHeight: '200px' }}>
    <div className="loading-screen__spinner" />
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<ProtectedRoute allowUnverified />}>
            <Route path="/verify-contact" element={<VerifyContactPage />} />
          </Route>
          <Route element={<AppShell />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route element={<ProtectedRoute roles={['admin', 'owner']} />}>
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/activity" element={<ActivityTimelinePage />} />
            </Route>
            <Route element={<ProtectedRoute roles={['admin', 'owner', 'coach', 'student']} />}>
              <Route path="/classes" element={<ClassesPage />} />
              <Route path="/classes/:classId" element={<ClassDetailPage />} />
              <Route path="/sessions/:sessionId" element={<SessionDetailPage />} />
              <Route path="/schedule" element={<Navigate to="/classes" replace />} />
            </Route>
            <Route element={<ProtectedRoute roles={['admin', 'owner', 'coach', 'student']} />}>
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:eventId" element={<EventsPage />} />
            </Route>
            <Route path="/billing" element={<BillingPage />} />
            <Route element={<ProtectedRoute roles={['admin', 'owner', 'coach']} />}>
              <Route path="/attendance" element={<AttendancePage />} />
            </Route>
            <Route element={<ProtectedRoute roles={['admin', 'owner']} />}>
              <Route path="/people" element={<PeoplePage />} />
            </Route>
            <Route element={<ProtectedRoute roles={['admin', 'owner']} />}>
              <Route path="/enrollments" element={<EnrollmentsPage />} />
            </Route>
            <Route element={<ProtectedRoute roles={['admin', 'owner', 'student']} />}>
              <Route path="/leave" element={<LeavePage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
            </Route>
            <Route element={<ProtectedRoute roles={['owner', 'admin']} />}>
              <Route path="/settings" element={<InstituteSettingsPage />} />
            </Route>
            <Route path="/profile" element={<ProfilePage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  const Router = isDemoMode ? HashRouter : BrowserRouter;

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
        <PWAInstallBanner />
      </AuthProvider>
    </ThemeProvider>
  );
}

